/**
 * Select component based on Radix UI Select primitive.
 *
 * A flexible select component with variant and size support, built on top of Radix UI
 * for accessibility and keyboard navigation.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Select>
 *   <SelectTrigger>
 *     <SelectValue placeholder="Select an option" />
 *   </SelectTrigger>
 *   <SelectContent>
 *     <SelectItem value="option1">Option 1</SelectItem>
 *     <SelectItem value="option2">Option 2</SelectItem>
 *   </SelectContent>
 * </Select>
 *
 * // With variant
 * <Select>
 *   <SelectTrigger variant="ghost">Ghost select</SelectTrigger>
 *   {/* ... *\/}
 * </Select>
 * <Select>
 *   <SelectTrigger variant="emerald">Emerald focus</SelectTrigger>
 *   {/* ... *\/}
 * </Select>
 * <Select>
 *   <SelectTrigger variant="purple">Purple focus</SelectTrigger>
 *   {/* ... *\/}
 * </Select>
 * <Select>
 *   <SelectTrigger variant="gold">Gold focus</SelectTrigger>
 *   {/* ... *\/}
 * </Select>
 * <Select>
 *   <SelectTrigger variant="pink">Pink focus</SelectTrigger>
 *   {/* ... *\/}
 * </Select>
 *
 * // With size
 * <Select>
 *   <SelectTrigger size="sm">Small select</SelectTrigger>
 *   {/* ... *\/}
 * </Select>
 * ```
 */
import * as SelectPrimitive from '@radix-ui/react-select';
import { cva, type VariantProps } from 'class-variance-authority';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { motion } from 'motion/react';
import * as React from 'react';

import { cn } from '@/lib/utils';
import { MOTION_PRESETS, useMotionProps } from '@/lib/cva-motion';

/**
 * CVA variants for SelectTrigger component.
 *
 * Variants:
 * - default: Standard select with border
 * - ghost: Transparent background, minimal border, hover effect
 * - error: Red border indicating validation error
 * - emerald: Emerald focus ring
 * - purple: Purple focus ring
 * - gold: Gold focus ring
 * - pink: Pink focus ring
 *
 * Sizes:
 * - sm: Small (h-8, compact padding)
 * - md: Medium (h-10, default)
 * - lg: Large (h-12, increased padding)
 */
const selectTriggerVariants = cva(
  // text-base (≥16px) on mobile prevents iOS Safari focus zoom on form controls
  'group flex w-full items-center justify-between gap-2 rounded-xl border bg-white px-4 py-2.5 text-base font-medium shadow-sm transition-all duration-200 data-[placeholder]:text-muted-foreground hover:border-gray-300 hover:shadow focus:outline-none focus:ring-2 focus:ring-orange-peel/30 focus:ring-offset-2 focus:border-orange-peel disabled:cursor-not-allowed disabled:opacity-50 md:text-sm [&>span]:line-clamp-1',
  {
    variants: {
      variant: {
        default: 'border-gray-200',
        ghost: 'border-transparent bg-transparent shadow-none hover:bg-muted/50',
        error: 'border-destructive focus:ring-destructive/30',
        emerald: 'border-emerald/30 focus:ring-emerald/30',
        purple: 'border-purple/30 focus:ring-purple/30',
        gold: 'border-gold/30 focus:ring-gold/30',
        pink: 'border-pink/30 focus:ring-pink/30',
      },
      size: {
        sm: 'h-10 px-3 text-base md:h-9 md:text-xs',
        md: 'h-11 px-4',
        lg: 'h-12 px-4 text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
);

/**
 * Props for the SelectTrigger component.
 *
 * Extends Radix UI Select Trigger props and adds variant and size props.
 *
 * @property variant - Visual style variant (default, ghost, error, emerald, purple, gold, pink)
 * @property size - Size variant (sm, md, lg)
 */
export interface SelectTriggerProps
  extends
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>,
    VariantProps<typeof selectTriggerVariants> {}

/**
 * Root component for Select. Use this to control the select state.
 */
const Select = SelectPrimitive.Root;

/**
 * Group component for organizing SelectItems.
 */
const SelectGroup = SelectPrimitive.Group;

/**
 * Value component that displays the selected value or placeholder.
 */
const SelectValue = SelectPrimitive.Value;

/**
 * SelectTrigger component with variant and size support.
 *
 * Features:
 * - Type-safe variants using CVA
 * - Accessible via Radix UI primitives
 * - Keyboard navigation support
 * - Customizable styling
 *
 * @param props - SelectTrigger props including variant, size, and Radix UI props
 * @returns A styled select trigger element
 */
const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(({ className, variant, size, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(selectTriggerVariants({ variant, size }), className)}
    suppressHydrationWarning
    {...props}
    type='button'
  >
    {children}
    <SelectPrimitive.Icon className='inline-flex shrink-0 items-center justify-center'>
      <ChevronDown className='h-4 w-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180' />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

/**
 * Motion-enhanced SelectTrigger component with automatic animations.
 *
 * Provides the same API as SelectTrigger but with built-in motion animations:
 * - Glow effect on focus
 * - Subtle scale on hover
 * - Smooth focus transitions
 *
 * Note: This component uses Radix's asChild pattern to integrate motion animations.
 *
 * @example
 * ```tsx
 * <Select>
 *   <MotionSelectTrigger variant="emerald">
 *     <SelectValue placeholder="Select an option" />
 *   </MotionSelectTrigger>
 *   <SelectContent>
 *     <SelectItem value="option1">Option 1</SelectItem>
 *   </SelectContent>
 * </Select>
 * ```
 */
const MotionSelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(({ className, variant, size, children, ...props }, ref) => {
  const motionProps = useMotionProps(MOTION_PRESETS.input);

  return (
    <SelectPrimitive.Trigger
      ref={ref}
      type='button'
      className={cn(selectTriggerVariants({ variant, size }), className)}
      suppressHydrationWarning
      asChild
      {...props}
    >
      <motion.button
        {...(motionProps as any)}
        className={cn(selectTriggerVariants({ variant, size }), className)}
      >
        {children}
        <SelectPrimitive.Icon className='inline-flex shrink-0 items-center justify-center opacity-50'>
          <ChevronDown className='h-4 w-4' />
        </SelectPrimitive.Icon>
      </motion.button>
    </SelectPrimitive.Trigger>
  );
});
MotionSelectTrigger.displayName = 'MotionSelectTrigger';

/**
 * Scroll up button for Select dropdown when content overflows.
 */
const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <ChevronUp className='h-4 w-4' />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

/**
 * Scroll down button for Select dropdown when content overflows.
 */
const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <ChevronDown className='h-4 w-4' />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

/**
 * Content container for Select dropdown menu.
 *
 * Features:
 * - Portal rendering for proper z-index layering
 * - Animated entrance/exit
 * - Responsive positioning
 * - Scrollable when content overflows
 *
 * @param position - Positioning strategy ('popper' or 'item-aligned')
 */
const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', collisionPadding, ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        // Above the fixed marketing header (z-[200]) so options are not covered.
        // max-height must use var(); a bare custom property is invalid and the list
        // then grows under the header.
        'relative z-[220] max-h-[var(--radix-select-content-available-height)] min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-xl border border-gray-200 bg-white py-1.5 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-select-content-transform-origin]',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        className,
      )}
      position={position}
      // Clear the fixed 4rem header so the first options stay on screen.
      collisionPadding={collisionPadding ?? { top: 80, bottom: 16 }}
      suppressHydrationWarning
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          'p-1.5',
          position === 'popper' && 'w-full min-w-[var(--radix-select-trigger-width)]',
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

/**
 * Label component for Select groups.
 *
 * Use this to label groups of SelectItems.
 */
const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('py-1.5 pl-8 pr-2 body-small font-semibold', className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

/**
 * Individual selectable item within Select dropdown.
 *
 * Features:
 * - Keyboard navigation support
 * - Visual indicator when selected
 * - Disabled state support
 * - Accessible via Radix UI
 */
const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-pointer select-none items-center rounded-lg py-2.5 pl-9 pr-3 text-sm outline-none transition-colors data-[highlighted]:bg-orange-peel/10 data-[highlighted]:text-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  >
    <span className='absolute left-2.5 flex h-4 w-4 items-center justify-center'>
      <SelectPrimitive.ItemIndicator>
        <Check className='h-4 w-4 text-orange-peel' strokeWidth={2.5} />
      </SelectPrimitive.ItemIndicator>
    </span>

    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

/**
 * Separator component for visually dividing SelectItems.
 *
 * Use this to create visual separation between groups of items.
 */
const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-muted', className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  MotionSelectTrigger,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  selectTriggerVariants,
};
